import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Inbox, Briefcase, ArrowRight, DollarSign, Calendar, Plus, Trash2 } from "lucide-react";
import { UpcomingEventsCard } from "@/components/EventsWidgets";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MentorDashboard() {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mentorProfile, setMentorProfile] = useState(null);
  const [rate, setRate] = useState("");
  const [windows, setWindows] = useState([]);

  useEffect(() => {
    api.get("/mentor/idea-requests").then((r) => setReqs(r.data)).catch(() => {});
    api.get("/mentor/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/mentor/me").then((r) => {
      setMentorProfile(r.data);
      setRate(r.data?.hourly_rate_npr ?? "");
      setWindows(r.data?.availability_windows || []);
    }).catch(() => {});
  }, []);

  const saveRate = async () => {
    try {
      const val = rate === "" ? null : parseFloat(rate);
      const r = await api.put("/mentor/me/rate", { hourly_rate_npr: val });
      setMentorProfile((m) => m ? { ...m, hourly_rate_npr: r.data.hourly_rate_npr } : m);
      toast.success(val == null ? "Rate cleared" : `Rate saved — NPR ${val}/hr`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const saveWindows = async (next) => {
    try {
      const r = await api.put("/mentor/me/availability", { windows: next });
      setWindows(r.data.windows || []);
      toast.success("Availability saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const addWindow = () => setWindows((w) => [...w, { day: 1, start: "09:00", end: "12:00", tz: "Asia/Kathmandu" }]);
  const patchWindow = (i, patch) => setWindows((w) => w.map((x, j) => j === i ? { ...x, ...patch } : x));
  const removeWindow = (i) => { const next = windows.filter((_, j) => j !== i); setWindows(next); saveWindows(next); };

  const pending = reqs.filter((r) => r.status === "pending_mentor");

  return (
    <div data-testid="mentor-dashboard">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MENTOR / TUTOR WORKSPACE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">
        Welcome, <em className="italic text-kb-primary">{user?.name?.split(" ")[0] || "Mentor"}</em>.
      </h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-xl">
        Review project requests, mentor assigned researchers, approve milestones and leave structured feedback.
      </p>

      {mentorProfile && (
        <details className="mt-6 kb-card" data-testid="mentor-settings">
          <summary className="list-none cursor-pointer flex items-center gap-3 p-4 hover:bg-slate-50">
            <DollarSign className="w-4 h-4 text-kb-primary" />
            <div className="min-w-0 flex-1">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RATE &amp; AVAILABILITY · QUICK SETTINGS</div>
              <div className="text-xs text-kb-ink-muted">Hourly rate NPR {rate || "—"} · {windows.length} availability window{windows.length === 1 ? "" : "s"}. Click to edit.</div>
            </div>
          </summary>
          <div className="border-t border-slate-200 p-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3" data-testid="mentor-rate-card">
              <div className="min-w-0">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">HOURLY RATE (NPR)</div>
                <div className="text-xs text-kb-ink-muted">Guidance requests auto-quote against this figure. Leave blank to have admin price manually.</div>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <input type="number" min="0" step="50" className="kb-input max-w-[140px]" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="e.g. 2500" data-testid="mentor-rate-input" />
                <button onClick={saveRate} className="kb-btn-primary text-xs" data-testid="mentor-rate-save">Save</button>
              </div>
            </div>
            <div data-testid="mentor-availability-card">
              <div className="flex items-center gap-3 flex-wrap">
                <Calendar className="w-5 h-5 text-kb-primary" />
                <div className="min-w-0 flex-1">
                  <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">RECURRING AVAILABILITY</div>
                  <div className="text-xs text-kb-ink-muted">Clients see these windows on your public profile and use them as hints when proposing slots.</div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/mentors/${mentorProfile.id}`} target="_blank" className="kb-btn-outline text-xs">View public profile</Link>
                  <button onClick={addWindow} className="kb-btn-outline text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Window</button>
                </div>
              </div>
              {windows.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {windows.map((w, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-2 border border-slate-200 rounded-sm px-2 py-1.5">
                      <select className="kb-input text-xs py-1 max-w-[110px]" value={w.day} onChange={(e) => patchWindow(i, { day: parseInt(e.target.value, 10) })}>
                        {DAY_LABELS.map((d, di) => <option key={di} value={di}>{d}</option>)}
                      </select>
                      <input type="time" className="kb-input text-xs py-1 max-w-[110px]" value={w.start} onChange={(e) => patchWindow(i, { start: e.target.value })} />
                      <span className="kb-mono text-[10px] text-kb-ink-muted">to</span>
                      <input type="time" className="kb-input text-xs py-1 max-w-[110px]" value={w.end} onChange={(e) => patchWindow(i, { end: e.target.value })} />
                      <button onClick={() => saveWindows(windows)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline">Save</button>
                      <button onClick={() => removeWindow(i)} className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
              {windows.length === 0 && <div className="text-xs text-kb-ink-muted mt-3">No windows yet. Add one to hint clients on your preferred meeting times.</div>}
            </div>
          </div>
        </details>
      )}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        {[
          { k: pending.length, l: "Pending Decisions", icon: Inbox },
          { k: projects.length, l: "Assigned Projects", icon: Briefcase },
          { k: reqs.filter(r => r.status === "accepted").length, l: "Accepted" },
          { k: reqs.filter(r => r.status === "rejected").length, l: "Declined" },
        ].map((s, i) => (
          <div key={i} className="bg-white p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-light">METRIC</div>
            <div className="font-mono text-3xl text-kb-primary mt-3">{s.k}</div>
            <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <div className="kb-card p-6">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PENDING DECISIONS</div>
            <Link to="/mentor/requests" className="kb-mono text-xs text-kb-primary">VIEW ALL →</Link>
          </div>
          {pending.length === 0 ? (
            <div className="mt-4 text-sm text-kb-ink-muted">No pending decisions. You&apos;re up to date.</div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-200">
              {pending.slice(0, 5).map((r) => (
                <li key={r.id} className="py-3" data-testid={`mentor-pending-${r.id}`}>
                  <div className="text-kb-ink font-medium line-clamp-1">{r.idea_title}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-light mt-0.5">From {r.client_name} · {new Date(r.sent_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="kb-card p-6">
          <div className="flex items-center justify-between">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ASSIGNED PROJECTS</div>
            <Link to="/mentor/projects" className="kb-mono text-xs text-kb-primary">VIEW ALL →</Link>
          </div>
          {projects.length === 0 ? (
            <div className="mt-4 text-sm text-kb-ink-muted">No assigned projects yet.</div>
          ) : (
            <ul className="mt-4 divide-y divide-slate-200">
              {projects.slice(0, 5).map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3" data-testid={`mentor-project-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <Link to={`/mentor/ideas/${p.id}`} className="block text-kb-ink hover:text-kb-primary font-medium truncate">{p.title}</Link>
                    <div className="kb-mono text-[10px] text-kb-ink-light">{p.domain} · {p.user_name}</div>
                  </div>
                  <span className="kb-mono text-[10px] uppercase text-kb-primary bg-kb-primary/5 border border-kb-primary/15 px-2 py-1 rounded-sm">{p.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8">
        <UpcomingEventsCard title="Live webinars & workshops" />
      </div>
    </div>
  );
}
